// TRCA assessment canonical schema — shared by form UI and results display.
// Mirrors backend constants in server.py. Each scored question option = 1 point.

export const BUSINESS_PROFILE = {
  business_type: {
    label: "Business Type",
    type: "multi",
    options: [
      "Manufacturer",
      "Contractor / EPC",
      "Service Provider",
      "Trader / Distributor",
      "Exporter / Importer",
    ],
  },
  industries: {
    label: "Major Industries / Sectors served",
    type: "multi_with_other",
    options: [
      "Construction & Civil (roads, bridges, housing, water, railways)",
      "Infrastructure (airports, ports, metro, highways, logistics)",
      "Energy & Power (oil & gas, solar, wind, power plants, distribution)",
      "Healthcare & Pharma (hospitals, medical equipment, pharmaceuticals)",
      "IT & Telecom (software, IT services, telecom, e-governance)",
    ],
  },
  annual_turnover: {
    label: "Annual Turnover",
    type: "single",
    options: [
      "Less than ₹5 Cr",
      "₹5 Cr – ₹25 Cr",
      "₹25 Cr – ₹50 Cr",
      "₹50 Cr – ₹100 Cr",
      "Above ₹100 Cr",
    ],
  },
  tender_volume: {
    label: "Tender Participation Volume",
    type: "single",
    options: [
      "Less than 5 tenders per year",
      "5 – 15 tenders per year",
      "15 – 25 tenders per year",
      "25 – 50 tenders per year",
      "More than 50 tenders per year",
    ],
  },
  win_rate: {
    label: "Current Tender Win Rate",
    type: "single",
    required: true,
    options: ["Less than 5%", "5% – 15%", "15% – 35%", "35% – 50%", "More than 50%"],
  },
};

// Scored sections (Sections 3–8). Each option = 1 point.
export const SCORED_SECTIONS = [
  {
    key: "compliance",
    title: "Compliance & Eligibility Readiness",
    max: 25,
    questions: [
      {
        key: "q1",
        label: "Registrations / Approvals",
        prompt: "Which of the following registrations/approvals are currently active and valid?",
        options: [
          "Udyam MSME Registration",
          "GST Registration with regular filing",
          "EPF & ESIC Registration",
          "PAN/TAN & valid Bank Account linkage",
          "IEC / Export readiness",
        ],
      },
      {
        key: "q2",
        label: "Quality & Compliance Certifications",
        prompt: "Which quality & compliance certifications does your firm currently hold?",
        options: [
          "ISO 9001:2015",
          "ISO 14001 / OHSAS 18001 / ISO 45001",
          "CE Marking / BIS Certification",
          "G-Mark Certification",
          "Any other relevant certification (API, NSIC, SSI, Star Export House)",
        ],
      },
      {
        key: "q3",
        label: "Statutory & Financial Documents Readiness",
        prompt: "Which statutory/financial documents are readily available?",
        options: [
          "Audited Financial Statements (last 3 years)",
          "GST Returns & ITRs (last 3 years)",
          "Work Completion Certificates & Performance Reports",
          "Credit Rating / Solvency Certificate / Bank Facility Letter",
          "Insurance Coverage documents (indemnity, product liability, workmen's compensation)",
        ],
      },
      {
        key: "q4",
        label: "Institutional Compliance",
        prompt: "Do you have any of the following in place?",
        options: [
          "Valid MSE / SC/ST / Women-owned certificate",
          "GeM Seller Registration",
          "Product / Service Catalogues uploaded in GeM portal",
          "Valid Aadhaar-linked DSC (Class 3)",
          "Regular compliance audit / internal checklist",
        ],
      },
      {
        key: "q5",
        label: "Special Licenses & Sustainability Clearances",
        prompt: "Do you hold any of the following special licenses/clearances?",
        options: [
          "NSIC Registration (for EMD waivers, tender benefits)",
          "Pollution Control / Environmental Clearance",
          "Factory License / Labour Compliance Certificates",
          "Sustainability initiatives (carbon credits, eco-friendly certifications, e-waste management compliance)",
          "Industry-specific approvals (e.g., PESO, CEA, Infra safety norms)",
        ],
      },
    ],
  },
  {
    key: "discovery",
    title: "Tender Discovery & Tracking",
    max: 15,
    questions: [
      {
        key: "q6",
        label: "Other Tender Portals Registered",
        prompt: "Which portals are you registered on, apart from GeM?",
        options: [
          "Central Public Procurement Portal (CPPP)",
          "State e-Procurement portals",
          "PSU portals",
          "Private client portals",
          "International portals",
        ],
      },
      {
        key: "q7",
        label: "Tender Tracking",
        prompt: "How do you track upcoming tenders?",
        options: [
          "GeM notifications",
          "National subscription services",
          "National + international subscriptions",
          "Direct client invitations",
          "Association / chamber alerts",
        ],
      },
      {
        key: "q8",
        label: "Tender Dashboard / Monitoring System",
        prompt: "Which practices do you follow for monitoring tenders?",
        options: [
          "Maintain dashboard (spreadsheet / ERP / analytics)",
          "Update weekly",
          "Track critical dates",
          "Send alerts to team",
          "Hold weekly review meetings",
        ],
      },
    ],
  },
  {
    key: "bid_process",
    title: "Bid Process & Governance",
    max: 20,
    questions: [
      {
        key: "q9",
        label: "Tender Involvement",
        prompt: "Which practices do you follow before bidding?",
        options: [
          "Attend pre-tender meetings",
          "Participate in site visits / pre-bid discussions",
          "Raise queries / clarifications",
          "Scope review by operations team",
          "Contract / legal review",
        ],
      },
      {
        key: "q10",
        label: "Bid Documentation",
        prompt: "Which practices are part of your bidding process?",
        options: [
          "Prepare key tender summary / bid notes",
          "Use standard technical templates",
          "Follow structured tender checklist",
          "Conduct post-tender analysis",
          "Capture lessons learned from past bids",
        ],
      },
      {
        key: "q11",
        label: "Structured Bid/No-Bid Process",
        prompt: "Which practices do you follow?",
        options: [
          "Eligibility screening",
          "Financial feasibility check",
          "Strategic fit assessment",
          "Formal management approval",
          "Defined bid/no-bid decision framework",
        ],
      },
      {
        key: "q12",
        label: "Risk Assessment",
        prompt: "Do you conduct risk assessment for the following areas?",
        options: [
          "Strategic exposure (market, competition, positioning)",
          "Contractual exposure",
          "Commercial exposure",
          "Operational exposure",
          "Overall combined risk assessment / report",
        ],
      },
    ],
  },
  {
    key: "commercial",
    title: "Commercial & Strategy Capability",
    max: 10,
    questions: [
      {
        key: "q13",
        label: "Pricing Capabilities",
        prompt: "Which practices do you follow in pricing?",
        options: [
          "Early alignment with vendors / OEMs / subcontractors / JVs",
          "Use updated norms / man-hour standards",
          "Prepare vetted cost breakdowns",
          "Evaluate tender cashflow",
          "Include quantified risk provisions and contingencies",
        ],
      },
      {
        key: "q14",
        label: "Tender Winning Efforts",
        prompt: "Which practices improve your winning chances?",
        options: [
          "Build client relationships and intelligence",
          "Use past data and competitor analysis",
          "Apply structured bidding strategies / frameworks",
          "Leverage brand / positioning advantage",
          "Add differentiators (innovation, sustainability, service quality)",
        ],
      },
    ],
  },
  {
    key: "execution",
    title: "Execution Readiness",
    max: 20,
    questions: [
      {
        key: "q15",
        label: "Tender Expertise",
        prompt: "Do you have relevant tendering expertise?",
        options: [
          "Dedicated tendering team",
          "Experienced Tendering Manager",
          "External consultants / advisors engaged",
          "Regular training for tendering staff",
          "Knowledge of PSU / international tendering norms",
        ],
      },
      {
        key: "q16",
        label: "Financial Capability",
        prompt: "Which practices strengthen your financial readiness?",
        options: [
          "Access to working capital / credit lines",
          "Structured cost control systems",
          "Competitive pricing strategies",
          "Financial risk hedging (insurance, guarantees)",
          "Strong balance sheet and liquidity",
        ],
      },
      {
        key: "q17",
        label: "Operational Readiness",
        prompt: "Which practices ensure operational readiness for tender execution?",
        options: [
          "Material sourcing and supplier arrangements in place",
          "Skilled manpower availability and deployment readiness",
          "Access to required equipment / tools / infrastructure",
          "Logistics and delivery arrangements established",
          "Contingency planning for disruptions in place",
        ],
      },
      {
        key: "q18",
        label: "International Tender Readiness",
        prompt: "Which practices show readiness for global tenders?",
        options: [
          "Registration on global portals (World Bank, UN, GCC, etc.)",
          "Ability to prepare bids in foreign languages",
          "International JV / partnership experience",
          "Compliance with global standards / certifications",
          "Cross-border logistics / finance capability",
        ],
      },
    ],
  },
  {
    key: "growth",
    title: "Growth & Performance",
    max: 10,
    questions: [
      {
        key: "q19",
        label: "Market Expansion Efforts",
        prompt: "Which practices support expansion in tender participation?",
        options: [
          "Explore new geographies / regions",
          "Diversify into new industries / sectors",
          "Build partnerships / JVs for expansion",
          "Invest in marketing / branding for tenders",
          "Participate in trade fairs / exhibitions",
        ],
      },
      {
        key: "q20",
        label: "Tender Performance Review & Improvement System",
        prompt: "Which of the following practices are followed to improve tendering performance?",
        options: [
          "Track win/loss reasons for each tender",
          "Maintain a structured lessons-learned repository",
          "Review tender performance periodically (monthly / quarterly)",
          "Maintain client engagement and capture project outcome feedback",
          "Update strategies based on past outcomes and feedback",
        ],
      },
    ],
  },
];

export const ALL_QUESTIONS = SCORED_SECTIONS.flatMap((s) => s.questions.map((q) => ({ ...q, sectionKey: s.key })));

export const optionScore = (q, opt) => {
  const i = q.options.indexOf(opt);
  return i < 0 ? 0 : (q.option_scores ? Number(q.option_scores[i]) || 0 : 1);
};
export const questionScore = (q, picked) => Math.round((picked || []).reduce((a, o) => a + optionScore(q, o), 0) * 100) / 100;

let schemaPromise = null;
// Replaces the built-in questions with the admin-edited active version from the server (in place).
export function loadTrcaSchema(api) {
  if (!schemaPromise) {
    schemaPromise = api.get("/trca/schema").then(({ data }) => {
      if (Array.isArray(data?.sections) && data.sections.length) {
        SCORED_SECTIONS.splice(0, SCORED_SECTIONS.length, ...data.sections);
        ALL_QUESTIONS.splice(0, ALL_QUESTIONS.length, ...SCORED_SECTIONS.flatMap((s) => s.questions.map((q) => ({ ...q, sectionKey: s.key }))));
      }
    }).catch(() => { schemaPromise = null; });
  }
  return schemaPromise;
}

export function computeScore(answers) {
  let total = 0;
  const sectionScores = {};
  for (const s of SCORED_SECTIONS) {
    let st = 0;
    for (const q of s.questions) {
      st += questionScore(q, answers?.[q.key]);
    }
    sectionScores[s.key] = Math.round(st * 100) / 100;
    total += st;
  }
  return { total: Math.round(total), sectionScores };
}

export const BANDS = [
  { min: 0, max: 30, name: "Emerging", desc: "Foundational tender readiness gaps. We recommend a structured TRCA consulting engagement.", color: "#FF5A00" },
  { min: 31, max: 60, name: "Developing", desc: "Reasonable groundwork in place, with clear opportunities to professionalize bid processes.", color: "#FF8A00" },
  { min: 61, max: 85, name: "Established", desc: "Strong tender ecosystem with targeted improvement areas.", color: "#002FA7" },
  { min: 86, max: 100, name: "Advanced", desc: "Market-leading tender readiness. Focus on scaling and global expansion.", color: "#009E60" },
];

export function getBand(score) {
  return BANDS.find((b) => score >= b.min && score <= b.max) || BANDS[0];
}
